import { Stack, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type { ReactNode } from "react";
import type { HealNTrackQueueFilters } from "@/api/endpoints";
import { startOfDayUtc, endOfDayUtc } from "@/shared/datetime";

/** Local ISO date (yyyy-MM-dd) for the date inputs — never UTC, which shifts the day. */
function isoDate(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

/**
 * Empty strings would be sent as blank query params; strip them to "no filter".
 *
 * The dates are also converted from the day the user picked *in the display zone* to the exact UTC
 * instants that bound it. The stored timestamps are UTC, so sending a bare date would filter UTC
 * midnight to UTC midnight — in IST that is 05:30 to 05:30, and orders from the first hours of the
 * day would be missing from a result set that still shows them as today.
 */
export function toQueryFilters(f: HealNTrackQueueFilters): HealNTrackQueueFilters {
  return {
    patient: f.patient?.trim() || undefined,
    doctor: f.doctor?.trim() || undefined,
    fromDate: f.fromDate ? startOfDayUtc(f.fromDate) : undefined,
    toDate: f.toDate ? endOfDayUtc(f.toDate) : undefined,
  };
}

export const emptyQueueFilters: HealNTrackQueueFilters = {
  patient: "", doctor: "", fromDate: "", toDate: "",
};

/** Today in both date boxes — the default view a counter wants on a busy day. */
export function todayQueueFilters(): HealNTrackQueueFilters {
  const today = isoDate(new Date());
  return { patient: "", doctor: "", fromDate: today, toDate: today };
}

/**
 * The filter row shared by the pharmacy, lab and radiology HealNTrack queues, so the three read
 * and behave identically. `leading` takes each queue's own selects (status, discipline).
 *
 * Patient accepts a UHID *or* a mobile number — the counter has whichever the patient gives them,
 * and asking staff to know which box to use is how the wrong one gets typed.
 */
export function QueueFilterBar({
  value, onChange, leading,
}: {
  value: HealNTrackQueueFilters;
  onChange: (next: HealNTrackQueueFilters) => void;
  leading?: ReactNode;
}) {
  const set = (patch: Partial<HealNTrackQueueFilters>) => onChange({ ...value, ...patch });
  const isDirty = !!(value.patient || value.doctor || value.fromDate || value.toDate);

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
      {leading}
      <TextField
        label="Patient" size="small" sx={{ minWidth: 220 }}
        placeholder="UHID or mobile number"
        value={value.patient ?? ""}
        onChange={(e) => set({ patient: e.target.value })}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        inputProps={{ "data-testid": "queue-filter-patient" }}
      />
      <TextField
        label="Doctor" size="small" sx={{ minWidth: 200 }}
        placeholder="Name or id"
        value={value.doctor ?? ""}
        onChange={(e) => set({ doctor: e.target.value })}
        inputProps={{ "data-testid": "queue-filter-doctor" }}
      />
      <TextField
        label="From" type="date" size="small" sx={{ minWidth: 160 }}
        InputLabelProps={{ shrink: true }}
        value={value.fromDate ?? ""}
        onChange={(e) => set({ fromDate: e.target.value })}
        inputProps={{ "data-testid": "queue-filter-from" }}
      />
      <TextField
        label="To" type="date" size="small" sx={{ minWidth: 160 }}
        InputLabelProps={{ shrink: true }}
        value={value.toDate ?? ""}
        onChange={(e) => set({ toDate: e.target.value })}
        inputProps={{ "data-testid": "queue-filter-to" }}
      />
      <Button size="small" onClick={() => onChange(emptyQueueFilters)} disabled={!isDirty}
        data-testid="queue-filter-clear">
        Clear
      </Button>
    </Stack>
  );
}
